/**
 * Slider - Lightweight carousel/slider/gallery utility
 *
 * Provides smooth scrolling with two interaction modes:
 * - Snap mode (snap: true): Swipe detection, scroll by slide (for galleries)
 * - Drag mode (snap: false): 1:1 drag scrolling with momentum (for carousels)
 *
 * Supports keyboard navigation (Arrow Left/Right), prev/next buttons, optional autoplay,
 * and opt-in wrap-around navigation.
 *
 * Data attributes:
 * - [data-slider-scroll-target] - Scrollable container (required)
 * - [data-slider-item] - Individual slide/item elements (required)
 * - [data-slider-prev] - Previous button (optional)
 * - [data-slider-next] - Next button (optional)
 *
 * @class
 * @example
 * const slider = new Slider(element, { snap: true, autoplay: false });
 * slider.onScroll((index) => console.log('Current:', index));
 * slider.destroy();
 */
export default class Slider {
  /**
   * Skip swipe/drag handling for nested interactive content.
   *
   * @param {EventTarget|null} target
   * @returns {boolean}
   * @private
   */
  shouldIgnorePointerStart(target) {
    return target instanceof Element && Boolean(target.closest('[data-slider-no-drag]'));
  }

  /**
   * Initialize a new slider.
   *
   * @param {HTMLElement} root - Slider root element (parent of all data attributes)
   * @param {Object} options - Configuration options
   * @param {boolean} options.snap - Enable snap scrolling by slide (default: true)
   * @param {boolean} options.autoplay - Enable automatic sliding (default: false)
   * @param {number} options.interval - Autoplay interval in milliseconds (default: 5000)
   * @param {boolean} options.wrap - Wrap to opposite end when advancing past edges
   * @param {boolean} options.rootTabStop - Make the root focusable so arrow-key
   *   nav works when slides hold no focusable elements (default: true). Pass
   *   false when every slide is keyboard-reachable on its own (e.g. product
   *   cards) — the keydown handler still fires via bubbling from the focused
   *   child, and the root would only add an empty tab stop.
   */
  constructor(root, options = {}) {
    this.root = root;
    this.container = root.querySelector('[data-slider-scroll-target]');
    this.items = root.querySelectorAll('[data-slider-item]');
    this.prevBtn = root.querySelector('[data-slider-prev]');
    this.nextBtn = root.querySelector('[data-slider-next]');

    this.options = {
      snap: true,
      autoplay: false,
      interval: 5000,
      wrap: false,
      rootTabStop: true,
      ...options
    };

    this.autoplayTimer = null;

    if (this.container) this.init();
  }

  /**
   * Initialize slider: bind event listeners, setup controls, start autoplay if configured.
   *
   * @returns {void}
   * @private
   */
  init() {
    // One controller for every persistent listener this init cycle binds (on
    // the root, container, and prev/next buttons — all of which survive a
    // rebuild). destroy() aborts it so a destroy→re-init (variant/combined-
    // listing morph, refreshSettings) can't stack duplicate handlers that make
    // arrows and keyboard nav scroll multiple steps per press.
    this._abort = new AbortController();
    const { signal } = this._abort;

    // Controls
    this.prevBtn?.addEventListener('click', () => {
      this.scroll(-1);
      if (this.options.autoplay) this.resetAutoplay();
    }, { signal });
    this.nextBtn?.addEventListener('click', () => {
      this.scroll(1);
      if (this.options.autoplay) this.resetAutoplay();
    }, { signal });

    // Keyboard
    if (this.options.rootTabStop) this.root.tabIndex = 0;
    this.root.addEventListener('keydown', (e) => {
      // stopPropagation so a slider nested inside another slider's root (e.g.
      // the UGC gallery inside the product gallery) consumes its own arrow keys
      // instead of letting them bubble up and scroll the outer track too.
      if (e.key === 'ArrowLeft') {
        this.scroll(-1);
        if (this.options.autoplay) this.resetAutoplay();
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === 'ArrowRight') {
        this.scroll(1);
        if (this.options.autoplay) this.resetAutoplay();
        e.preventDefault();
        e.stopPropagation();
      }
    }, { signal });

    // Mouse drag
    this.options.snap ? this.bindSwipe() : this.bindDrag();

    this.container.classList.add('scrollbar-hide');

    this.updateControls();
    this._scrollHandler = () => this.updateControls();
    this.container.addEventListener('scroll', this._scrollHandler, { passive: true, signal });
    this._resizeHandler = () => this.updateControls();
    window.addEventListener('resize', this._resizeHandler, { signal });

    if (this.options.autoplay && this.items.length > 1) {
      this.startAutoplay();
    }
  }

  /**
   * Bind swipe detection (snap mode).
   * Detects left/right swipes and scrolls by one slide per swipe.
   * Threshold: 30px minimum movement to trigger.
   *
   * @returns {void}
   * @private
   */
  bindSwipe() {
    let startX = 0;
    let dragging = false;

    const { signal } = this._abort;

    this.container.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      if (this.shouldIgnorePointerStart(e.target)) return;
      dragging = true;
      startX = e.screenX;
      this.container.style.cursor = 'grabbing';
      e.preventDefault();
    }, { signal });

    this.container.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

    this._swipeMouseUpHandler = (e) => {
      if (!dragging) return;
      dragging = false;
      this.container.style.cursor = '';
      const diff = startX - e.screenX;
      if (Math.abs(diff) > 30) this.scroll(diff > 0 ? 1 : -1);
    };
    document.addEventListener('mouseup', this._swipeMouseUpHandler, { signal });
  }

  /**
   * Bind 1:1 drag scrolling (free scroll mode).
   * Uses document-level move/up so drags stay smooth when the pointer passes over
   * overlay controls (e.g. prev/next) that sit above the scroll track.
   * Prevents click events after drag to avoid unintended interactions.
   *
   * @returns {void}
   * @private
   */
  bindDrag() {
    /**
     * Prev/next arrows sit above the track (z-index). Listening on the container only
     * misses mousemove over those overlays and mouseleave ends the drag early — felt
     * worst when dragging back toward the start (pointer crosses the prev arrow).
     */
    this._dragDocMove = (e) => {
      if (!this._dragActive) return;
      const walk = e.clientX - this._dragStartX;
      if (Math.abs(walk) > 5) this._dragMoved = true;
      this.container.scrollLeft = this._dragScrollStart - walk;
    };

    this._snapToNearest = () => {
      const idx = this.getCurrentIndex();
      const item = this.items[idx];
      if (item) this.container.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
    };

    this._dragDocEnd = () => {
      if (!this._dragActive) return;
      this._dragActive = false;
      document.removeEventListener('mousemove', this._dragDocMove);
      document.removeEventListener('mouseup', this._dragDocEnd);

      this.container.style.cursor = '';
      this.container.style.userSelect = '';
      this.container.style.scrollBehavior = this._dragPrevScrollBehavior ?? '';
      this._dragPrevScrollBehavior = undefined;

      if (this.options.snapOnRelease && this._dragMoved) {
        this._snapToNearest();
      }

      if (this._dragMoved) {
        const block = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        };
        this.container.addEventListener('click', block, { capture: true, once: true });
        setTimeout(() => this.container.removeEventListener('click', block, true), 300);
      }
    };

    this._touchEnd = () => {
      this.container.removeEventListener('touchend', this._touchEnd);
      this.container.removeEventListener('touchcancel', this._touchEnd);
      this.container.removeEventListener('touchmove', this._touchMoveTrack);
      if (!this.options.snapOnRelease || !this._touchMoved) return;
      // Wait for native momentum scrolling to settle before snapping
      let settleTimer;
      const snap = () => {
        if (this._snapToNearest) this._snapToNearest();
      };
      const onScroll = () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          this.container.removeEventListener('scroll', onScroll);
          snap();
        }, 80);
      };
      this.container.addEventListener('scroll', onScroll, { passive: true });
      // Fallback: snap even if no scroll event fires (very short flick)
      settleTimer = setTimeout(() => {
        this.container.removeEventListener('scroll', onScroll);
        snap();
      }, 80);
    };

    this._touchMoveTrack = () => { this._touchMoved = true; };

    const { signal } = this._abort;

    this.container.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      if (this.shouldIgnorePointerStart(e.target)) return;
      this._dragActive = true;
      this._dragMoved = false;
      this._dragStartX = e.clientX;
      this._dragScrollStart = this.container.scrollLeft;
      this._dragPrevScrollBehavior = this.container.style.scrollBehavior;
      this.container.style.scrollBehavior = 'auto';
      this.container.style.cursor = 'grabbing';
      this.container.style.userSelect = 'none';
      e.preventDefault();
      document.addEventListener('mousemove', this._dragDocMove);
      document.addEventListener('mouseup', this._dragDocEnd);
    }, { signal });

    // Touch: let the browser scroll natively, snap after momentum settles
    this.container.addEventListener('touchstart', (e) => {
      if (this.shouldIgnorePointerStart(e.target)) return;
      this._touchMoved = false;
      this.container.addEventListener('touchmove', this._touchMoveTrack, { passive: true, once: true });
      this.container.addEventListener('touchend', this._touchEnd, { once: true });
      this.container.addEventListener('touchcancel', this._touchEnd, { once: true });
    }, { passive: true, signal });

    this.container.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
  }

  /**
   * Scroll by delta slides (snap mode) or by 25% container width (drag mode).
   * Uses smooth scrolling behavior.
   *
   * @param {number} delta - Number of slides to scroll (positive = right/next, negative = left/prev)
   * @returns {void}
   */
  scroll(delta) {
    if (this.options.snap || this.options.snapOnRelease) {
      // Scroll to next/prev item
      const idx = this.getCurrentIndex();
      let newIdx;
      if (this.options.wrap && this.items.length > 1) {
        newIdx = (idx + delta + this.items.length) % this.items.length;
      } else {
        newIdx = Math.max(0, Math.min(this.items.length - 1, idx + delta));
      }
      const item = this.items[newIdx];
      if (item) {
        const itemLeft = item.offsetLeft;
        this.container.scrollTo({ left: itemLeft, behavior: 'smooth' });
      }
    } else {
      // Scroll by 25% of container width
      this.container.scrollBy({ left: delta * this.container.clientWidth * 0.25, behavior: 'smooth' });
    }
  }

  /**
   * Get the index of the slide whose start best matches scroll position.
   * Nearest-by-offsetLeft avoids falling back to 0 when no slide contains the viewport
   * center (wide slides / partial scroll) — that broke prev in snap mode.
   *
   * @returns {number} Index of current slide (0-based)
   */
  getCurrentIndex() {
    if (!this.items.length) return 0;
    const sl = this.container.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < this.items.length; i++) {
      const left = this.items[i].offsetLeft;
      const dist = Math.abs(left - sl);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  /**
   * Register a scroll listener that fires when the slider finishes scrolling.
   * Includes 100ms debounce to avoid excessive callback invocations.
   *
   * @param {Function} callback - Called with current slide index when scroll completes
   * @param {number} callback.index - Current slide index
   * @param {Object} [options] - Optional listener options
   * @param {AbortSignal} [options.signal] - Signal used to remove listener and cancel pending debounce
   * @returns {void}
   */
  onScroll(callback, options) {
    let timeout;
    const clearPendingTimeout = () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    // Default to the slider's own controller so the scroll listener is removed
    // on destroy() even when the caller passes no signal — otherwise a consumer
    // that destroys + recreates on a persistent container (e.g. the UGC gallery)
    // stacks a scroll handler per reload. A caller-supplied signal still wins.
    const signal = options?.signal ?? this._abort?.signal;
    if (signal) {
      signal.addEventListener('abort', clearPendingTimeout, { once: true });
    }

    this.container.addEventListener('scroll', () => {
      clearPendingTimeout();
      timeout = setTimeout(() => {
        if (signal?.aborted) return;
        callback(this.getCurrentIndex());
      }, 100);
    }, { passive: true, signal });
  }

  /**
   * Start automatic sliding at configured interval.
   * Cycles through slides in order, wrapping to first slide after the last.
   *
   * @returns {void}
   */
  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      const idx = this.getCurrentIndex();
      const nextIdx = (idx + 1) % this.items.length;
      const item = this.items[nextIdx];
      if (item) {
        const itemLeft = item.offsetLeft;
        this.container.scrollTo({ left: itemLeft, behavior: 'smooth' });
      }
    }, this.options.interval);
  }

  /**
   * Stop autoplay and clear the timer.
   *
   * @returns {void}
   */
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * Restart autoplay timer (useful after manual interaction to resume autoplay).
   * Only restarts if autoplay is enabled in options.
   *
   * @returns {void}
   */
  resetAutoplay() {
    if (this.options.autoplay) {
      this.startAutoplay();
    }
  }

  /**
   * Re-query slide items from the DOM (call after dynamic content changes).
   * @returns {void}
   */
  refresh() {
    this.items = this.root.querySelectorAll('[data-slider-item]');
    this.updateControls();
  }

  /**
   * Scroll back to the first slide with no animation. Call after swapping in an
   * unrelated set of items (e.g. new search results replacing the previous
   * query's) so the fresh content doesn't inherit whatever scroll position the
   * old content happened to be left at.
   * @returns {void}
   */
  resetToStart() {
    if (!this.container) return;
    this.container.scrollLeft = 0;
  }

  updateControls() {
    if (!this.container) return;

    const tolerance = 2;
    const hasOverflow = this.container.scrollWidth > this.container.clientWidth + tolerance;

    if (this.prevBtn) {
      this.prevBtn.classList.toggle('hidden', !hasOverflow);
      this.prevBtn.classList.toggle('flex', hasOverflow);
    }
    if (this.nextBtn) {
      this.nextBtn.classList.toggle('hidden', !hasOverflow);
      this.nextBtn.classList.toggle('flex', hasOverflow);
    }

    if (!hasOverflow) return;

    if (this.options.wrap) {
      if (this.prevBtn) this.prevBtn.disabled = this.items.length <= 1;
      if (this.nextBtn) this.nextBtn.disabled = this.items.length <= 1;
      return;
    }

    const atStart = this.container.scrollLeft <= tolerance;
    const atEnd = this.container.scrollLeft + this.container.clientWidth >= this.container.scrollWidth - tolerance;

    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }

  /**
   * Clean up all event listeners and timers.
   * Should be called before discarding the Slider instance.
   *
   * @returns {void}
   */
  destroy() {
    this.stopAutoplay();

    // Removes every listener bound with this._abort.signal (all persistent
    // init/bindSwipe/bindDrag handlers) in one shot.
    this._abort?.abort();
    this._abort = null;
    this._scrollHandler = null;
    this._swipeMouseUpHandler = null;
    this._resizeHandler = null;

    // Per-gesture drag listeners live on document (bound at mousedown, outside
    // the signal), so tear down any drag caught in progress explicitly.
    if (this._dragDocMove) {
      document.removeEventListener('mousemove', this._dragDocMove);
      document.removeEventListener('mouseup', this._dragDocEnd);
      if (this.container && this._touchEnd) {
        this.container.removeEventListener('touchend', this._touchEnd);
        this.container.removeEventListener('touchcancel', this._touchEnd);
        this.container.removeEventListener('touchmove', this._touchMoveTrack);
      }
      this._dragDocMove = null;
      this._dragDocEnd = null;
      this._touchEnd = null;
      this._touchMoveTrack = null;
      this._snapToNearest = null;
    }
    this._dragActive = false;
    if (this.container && this._dragPrevScrollBehavior !== undefined) {
      this.container.style.scrollBehavior = this._dragPrevScrollBehavior;
      this._dragPrevScrollBehavior = undefined;
    }
  }
}
